<template>
    <div :class="['sidebar-container', { dark: theme == THEMES.DARK }]">
        <div class="logo-container">
            <router-link to="/dashboard" class="logo-link">
                <img class="logo" src="@/assets/logo-n.svg" alt="High Ties Cannabis Store" />
            </router-link>
            <button
                type="button"
                class="sidebar-collapse-button"
                @click="toggleShowSidebar"
                aria-label="Collapse navigation"
                title="Collapse navigation"
            >
                <i class="material-icons">keyboard_double_arrow_left</i>
            </button>
        </div>
        
        <div class="sidebar-content">
            <div class="sidebar-sections">
                <div 
                    v-for="(sidebarLinkGroup, i) of sidebarLinks" 
                    :key="i" 
                    class="sidebar-link-group"
                >
                    <SidebarLinkGroup 
                        :sidebarLinkGroup="mapIconsToMaterial(sidebarLinkGroup)" 
                        :key="i" 
                    />
                </div>
            </div>
            
            <div class="sidebar-footer">
                <div class="sidebar-footer-item">
                    <i class="material-icons">help_outline</i>
                    <span>Help & Resources</span>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script>
import SidebarLinkGroup from "./SidebarLinkGroup.vue"
import { mapState, mapActions } from "vuex"
import { PERMISSION_CODES, canViewLoyaltyReconciliation } from "../../store/permission"

export default {
    computed: {
        ...mapState("auth", ["authenticated", "user"]),
        ...mapState("misc", ["showSidebar", "theme", "THEMES"]),
    },
    components: { SidebarLinkGroup },
    data() {
        return {
            sidebarLinks: [
                {
                    title: "Dashboard",
                    icon: "dashboard",
                    activeIcon: "dashboard",
                    data: [
                        {
                            link: "/dashboard",
                            title: "Home",
                            permission: true,
                        },
                        {
                            link: "/dashboard/sales-revenue",
                            title: "Sales & Revenue",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_SALES_REPORT,
                        },
                        {
                            link: "/dashboard/inventory-operations",
                            title: "Inventory Operations",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_INVENTORY_ON_HAND,
                        },
                        {
                            link: "/dashboard/product-category",
                            title: "Product & Category",
                            permission: PERMISSION_CODES.CMS_VIEW_PRODUCT,
                        },
                        {
                            link: "/dashboard/compliance-data-quality",
                            title: "Compliance & Data Quality",
                            permission: PERMISSION_CODES.CMS_VIEW_WAREHOUSE,
                        },
                        {
                            link: "/dashboard/customer-loyalty",
                            title: "Customer & Loyalty",
                            permissions: [
                                PERMISSION_CODES.CMS_VIEW_REPORT_CUSTOMER,
                                PERMISSION_CODES.CMS_VIEW_REPORT_SALES_REPORT,
                                PERMISSION_CODES.CMS_VIEW_LOYALTY,
                            ],
                        },
                        {
                            link: "/dashboard/ecommerce",
                            title: "Ecommerce",
                            permission: PERMISSION_CODES.CMS_VIEW_PRODUCT,
                        },
                        {
                            link: "/dashboard/finance-management",
                            title: "Finance & Management",
                            permissions: [
                                PERMISSION_CODES.CMS_VIEW_REPORT_SALES_REPORT,
                                PERMISSION_CODES.CMS_VIEW_REPORT_CASH_MANAGEMENT,
                            ],
                        },
                    ],
                },
                {
                    title: "Orders & Customers",
                    icon: "shopping_cart",
                    activeIcon: "shopping_cart",
                    data: [
                        {
                            link: "/invoices",
                            title: "Invoices",
                            permission: PERMISSION_CODES.CMS_VIEW_INVOICE,
                        },
                        {
                            link: "/customers",
                            title: "Customers",
                            permission: PERMISSION_CODES.CMS_VIEW_CUSTOMER,
                        },
                        {
                            link: "/customer-create",
                            title: "Create Customer",
                            permission: PERMISSION_CODES.CMS_CREATE_CUSTOMER,
                        },
                        {
                            link: "/customer-import",
                            title: "Import Customers",
                            permission: PERMISSION_CODES.CMS_CUSTOMER_IMPORT,
                        },
                        {
                            link: "/customer-groups",
                            title: "Customer Groups",
                            permission: PERMISSION_CODES.CMS_VIEW_CUSTOMER_GROUP,
                        },
                        {
                            link: "/govt-ids",
                            title: "Government Ids",
                            permission: PERMISSION_CODES.CMS_VIEW_CUSTOMER_GOVT_ID,
                        },
                    ],
                },
                {
                    title: "Customer Engagement",
                    icon: "campaign",
                    activeIcon: "campaign",
                    data: [
                        {
                            link: "/promotions",
                            title: "Promotions",
                            matchPrefix: true,
                            permission: PERMISSION_CODES.CMS_VIEW_PROMOTION,
                        },
                        {
                            link: "/settings/account-automation",
                            title: "Account Automation",
                            permission: PERMISSION_CODES.CMS_VIEW_CUSTOMER,
                        },
                        {
                            link: "/settings/email-templates",
                            title: "Email Templates",
                            matchPrefix: true,
                            permission: PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE,
                        },
                        {
                            link: "/settings/newsletter-subscribers",
                            title: "Newsletter Subscribers",
                            permission: PERMISSION_CODES.CMS_VIEW_NEWSLETTER_SUBSCRIBER,
                        },
                        {
                            link: "/settings/loyalty-management",
                            title: "Loyalty Management",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            link: "/settings/loyalty",
                            title: "Loyalty Program",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            link: "/settings/loyalty-campaigns",
                            title: "Loyalty Campaigns",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            link: "/settings/loyalty-rewards",
                            title: "Loyalty Rewards",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY_REWARD,
                        },
                        {
                            link: "/settings/loyalty-tiers",
                            title: "Loyalty Tiers",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            link: "/settings/loyalty-health",
                            title: "Loyalty Health",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                        },
                        {
                            link: "/settings/loyalty-reconciliation",
                            title: "Loyalty Reconciliation",
                            permission: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                            // Managers can read the list without CMS_VIEW_LOYALTY.
                            visibleFor: canViewLoyaltyReconciliation,
                        },
                    ],
                },
                {
                    title: "Catalog & Inventory",
                    icon: "inventory",
                    activeIcon: "inventory",
                    data: [
                        {
                            link: "/item-groups",
                            title: "Categories",
                            permission: PERMISSION_CODES.CMS_VIEW_CATEGORY,
                        },
                        {
                            link: "/products",
                            title: "All Products",
                            permission: PERMISSION_CODES.CMS_VIEW_PRODUCT,
                        },
                        {
                            link: "/terpenes",
                            title: "Terpenes",
                            permission: PERMISSION_CODES.CMS_VIEW_TERPENE,
                        },
                        {
                            link: "/inventory-manager",
                            title: "Inventory Manager",
                            permission: PERMISSION_CODES.CMS_VIEW_INVENTORY_MANAGER,
                        },
                        {
                            link: "/inventory-adjustments",
                            title: "Adjustments",
                            permission: PERMISSION_CODES.CMS_VIEW_ADJUSTMENT,
                        },
                        {
                            link: "/purchase-orders",
                            title: "Purchase Order History",
                            permission: PERMISSION_CODES.CMS_VIEW_PURCHASE_ORDER,
                        },
                        {
                            link: "/stock-import",
                            title: "Create Purchase Order",
                            permission: PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER,
                        },
                        {
                            link: "/cova-import",
                            title: "Cova Sync",
                            permission: PERMISSION_CODES.CMS_COVA_STOCK_IMPORT,
                        },
                        {
                            link: "/import-history",
                            title: "Stock Entry History",
                            permission: PERMISSION_CODES.CMS_VIEW_STOCK_IMPORT_HISTORY,
                        },
                        {
                            link: "/stock-transfers",
                            title: "Transfers",
                            permission: PERMISSION_CODES.CMS_VIEW_TRANSFER,
                        },
                        {
                            link: "/print-labels",
                            title: "Print Labels",
                            permission: PERMISSION_CODES.CMS_PRINT_LABEL,
                        },
                    ],
                },
                {
                    title: "Warehouse",
                    icon: "warehouse",
                    activeIcon: "warehouse",
                    data: [
                        {
                            link: "/warehouses",
                            title: "Warehouses",
                            permission: PERMISSION_CODES.CMS_VIEW_WAREHOUSE,
                        },
                    ],
                },
                {
                    title: "Operations",
                    icon: "precision_manufacturing",
                    activeIcon: "precision_manufacturing",
                    data: [
                        {
                            link: "/cash-management",
                            title: "Cash Management",
                            permission: PERMISSION_CODES.CMS_VIEW_CASH_MANAGEMENT,
                        },
                        {
                            link: "/crew",
                            title: "Crew",
                            permission: PERMISSION_CODES.CMS_VIEW_CREW_MANAGEMENT,
                        },
                        {
                            link: "/crew-roles",
                            title: "Crew Roles",
                            permission: PERMISSION_CODES.CMS_VIEW_CREW_ROLE,
                        },
                    ],
                },
                {
                    title: "Reports",
                    icon: "bar_chart",
                    activeIcon: "bar_chart",
                    data: [
                        {
                            link: "/reports",
                            title: "All Reports",
                        },
                        {
                            link: "/reports/customer",
                            title: "Customer Reports",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_CUSTOMER,
                        },
                        {
                            link: "/reports/gl-transactions",
                            title: "GL Transactions",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_GL_TRANSACTIONS,
                        },
                        {
                            link: "/reports/inventory-on-hand",
                            title: "Inventory On Hand",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_INVENTORY_ON_HAND,
                        },
                        {
                            link: "/reports/sale-end-of-day",
                            title: "Sales Reports",
                            permission: PERMISSION_CODES.CMS_VIEW_REPORT_END_OF_DAY,
                        },
                        {
                            link: "/stock-logs",
                            title: "Stock Adjustment Logs",
                            permission: PERMISSION_CODES.CMS_VIEW_STOCK_UPDATE_LOG,
                        },
                    ],
                    isIndexUrl: true,
                },
                {
                    title: "Settings",
                    icon: "settings",
                    activeIcon: "settings",
                    data: [
                        {
                            link: "/gl-accounts",
                            title: "G/L Accounts",
                            permission: PERMISSION_CODES.CMS_VIEW_GL_ACCOUNT,
                        },
                        {
                            link: "/gl-defaults",
                            title: "G/L Defaults",
                            permission: PERMISSION_CODES.CMS_VIEW_GL_DEFAULT,
                        },
                        {
                            link: "/mode-of-payments",
                            title: "Payment Setup",
                            permission: PERMISSION_CODES.CMS_VIEW_PAYMENT_SETUP,
                        },
                        {
                            link: "/settings/retained-float",
                            title: "Drawer Retained Float",
                            permission: PERMISSION_CODES.CMS_VIEW_CASH_MANAGEMENT,
                        },
                        {
                            link: "/settings/checkout-policy",
                            title: "Require save before checkout",
                            permission: PERMISSION_CODES.CMS_VIEW_CASH_MANAGEMENT,
                        },
                        {
                            link: "/suppliers",
                            title: "Suppliers",
                            permission: PERMISSION_CODES.CMS_VIEW_SUPPLIER,
                        },
                        {
                            link: "/reason-codes",
                            title: "Reason Codes",
                            permission: PERMISSION_CODES.CMS_VIEW_REASON_CODE,
                        },
                        {
                            link: "/agco-reporting",
                            title: "AGCO Reporting",
                            permission: PERMISSION_CODES.CMS_VIEW_AGCO_REPORTING,
                        },
                        {
                            link: "/agco-logs/warehouses",
                            title: "AGCO Logs",
                            permission: PERMISSION_CODES.CMS_VIEW_AGCO_LOG,
                        },
                        {
                            link: "/reconciliations",
                            title: "Reconciliations",
                            permission: PERMISSION_CODES.CMS_VIEW_RECONCILIATION,
                        },
                        {
                            link: "/inventory-counts",
                            title: "Inventory Counts",
                            anyPermissions: [PERMISSION_CODES.CMS_VIEW_INVENTORY_COUNT, PERMISSION_CODES.CMS_VIEW_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_INVENTORY_COUNT, PERMISSION_CODES.CMS_CREATE_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER],
                        },
                        {
                            link: "/devices",
                            title: "Devices",
                            permission: PERMISSION_CODES.CMS_VIEW_DEVICE,
                        },
                        {
                            link: "/pos-app-releases",
                            title: "POS App Releases",
                            permission: PERMISSION_CODES.CMS_VIEW_DEVICE,
                        },
                        {
                            link: "/permissions",
                            title: "Permissions Info",
                            permission: false,
                        },
                        {
                            link: "/permissions/editor",
                            title: "Permissions Editor",
                            permission: false,
                        },
                        {
                            link: "/scanner-test",
                            title: "Scanner Test",
                            permission: false,
                        },
                    ],
                    isIndexUrl: true,
                },
            ],
        }
    },
    methods: {
        ...mapActions("misc", ["setData"]),

        toggleShowSidebar() {
            this.setData({
                showSidebar: !this.showSidebar,
            })
        },
        
        mapIconsToMaterial(sidebarLinkGroup) {
            // This function converts the SVG icon paths to Material Icon names
            // We're just passing through for now since we replaced the paths in the data
            return sidebarLinkGroup;
        }
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "@/assets/variables.scss";

.sidebar-container {
    width: $sidebarWidth;
    min-height: 100vh;
    height: 100vh;
    max-height: 100vh;
    background: linear-gradient(180deg, 
        $primaryColor 0%, 
        color.adjust($primaryColor, $lightness: -5%) 100%
    );
    color: white;
    display: flex;
    flex-direction: column;
    position: relative;
    top: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 200;
    
    /* Add subtle texture */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: repeating-linear-gradient(
            90deg,
            transparent,
            transparent 1px,
            rgba(255, 255, 255, 0.02) 1px,
            rgba(255, 255, 255, 0.02) 2px
        );
        pointer-events: none;
    }

    &.dark {
        background: linear-gradient(180deg, 
            $primaryColorDark 0%, 
            color.adjust($primaryColorDark, $lightness: -8%) 100%
        );
        box-shadow: 4px 0 20px rgba(0, 0, 0, 0.25);
    }
}

.logo-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 24px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    
    .logo-link {
        display: flex;
        align-items: center;
        min-width: 0;
        cursor: pointer;
        padding: 8px;
        border-radius: 12px;
        transition: all 0.3s ease;
        flex: 1 1 auto;
        
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
            transform: translateY(-1px);
        }
    }
    
    .logo {
        width: 160px;
        max-width: 100%;
        height: auto;
        /* Ensure logo appears properly white on dark background */
        filter: brightness(0) invert(1) brightness(1.1) contrast(1.2);
        transition: all 0.3s ease;
        
        .logo-link:hover & {
            filter: brightness(0) invert(1) brightness(1.3) contrast(1.3);
            transform: scale(1.02);
        }
    }
}

.sidebar-collapse-button {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    margin-left: 8px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.08);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

    i {
        font-size: 20px;
    }

    &:hover,
    &:focus-visible {
        background: rgba(255, 255, 255, 0.16);
        border-color: rgba(255, 255, 255, 0.35);
        transform: translateX(-1px);
        outline: none;
    }
}

.sidebar-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    padding: 16px 0 0;
}

.sidebar-sections {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 8px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.2);
        border-radius: 20px;
    }

    .sidebar-link-group {
        margin-bottom: 8px;
    }
}

.sidebar-footer {
    padding: 16px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    flex: 0 0 auto;
    margin-top: 0;
    
    .sidebar-footer-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
        
        i {
            opacity: 0.8;
        }
        
        span {
            font-size: 14px;
            font-weight: 500;
        }
    }
}

@media (max-width: 768px) {
    .sidebar-container {
        width: min(#{$sidebarWidth}, calc(100vw - 56px));
    }
}
</style>
